<h1 class="sectionHeader">Connecting Dash to a Google Workspace standard, or personal account</h1>

<p class="intro">
  Connecting Dash via the method described below is recommended for small businesses, or if you'd like to connect Dash to
  your personal Google account. If you are a larger business then we recommend that you can connect Dash via
  a <a routerLink="/guide/google-service-account">Service account</a>.
</p>

<h2>Creating a Google account for Dash</h2>
<p>
  Once you have set up your meeting rooms, you will need to choose a Google account from your organization to use with Dash (this can be
  either a new account or an existing one, for example <i>dash&#64;yourcompany.com</i>).  Follow the steps below to give this
  account access to your meeting room calendars.  You'll need to do this for each meeting room calendar you'd like to use with Dash.</p>
<ol class="instructionList">
  <li>
    <div>
      <p><b>Open the meeting room: </b><br/>Log into Google Calendar with an admin account for your organization.  You
        should see all your resource calendars listed under <i>My calendars</i>.  <br><br>Hover over one to see a three-dot menu
        appear.  Click the menu and select <i>Settings and sharing</i></p>
      <app-illustration class="illustration googleListCalendars" alt="Access your Google calendar's settings" src="../../assets/images/google-list-calendars.png" webpSrc="../../assets/images/google-list-calendars.webp" width="170" height="183" cover="true"></app-illustration>
    </div>
  </li>
  <li>
    <div>
      <p><b>Share the room's calendar: </b><br/>Scroll down until you see <i>Share with specific people or groups</i>
        and then click <i>Add people and groups</i>.  Enter the email address of the account that you chose to use
        with Dash.<br><br>If you plan to book, extend, or end meetings from your Dash display, select the
        <b>Make changes to events</b> permission and click <i>Send</i>. If you plan to use
        Dash as a read-only display then you can select <b>See all event details</b> instead.</p>
      <app-illustration class="illustration googleShareCalendar" alt="Share the calendar with your Dash account" src="../../assets/images/google-share-calendar.png" webpSrc="../../assets/images/google-share-calendar.webp" backgroundPosition="left" width="170" height="241" cover="true"></app-illustration>
    </div>
  </li>
  <li>
    <div>
      <p><b>Accept the share request: </b><br/>Log into Gmail with the account you created for Dash.  You should see a
        share notification email with a subject like <i>XXX has shared a calendar with you</i>.<br><br>Open the email and
        click the <i>Add this calendar</i> link.  This will add the meeting room to the account's list of calendars.</p>
      <app-illustration class="illustration googleAcceptShare" alt="Accept the share request, from your Google account" src="../../assets/images/google-accept-share.png" webpSrc="../../assets/images/google-accept-share.webp" backgroundPosition="left" width="170" height="244" cover="true"></app-illustration>
    </div>
  </li>
</ol>
<p class="tip icon">You can also use a free Gmail account instead of a Google account in your organization.  By default,
  Google only lets you share resource calendars with the <b>See all event details</b> permission (which won't let you book
  meetings from Dash) but you can change this in the Google Workspace Admin console.<br><br>Just go to <b>Apps > Google
    Workspace > Calendar > General settings > External sharing options for secondary calendars</b> and select <b>
    Share all information, and outsiders can change calendars</b>
</p>
<h2>Connecting Dash to your Google account</h2>
<p>
  Once you have set up your Google account, you can connect it to Dash.  Open Dash and go to Settings (the cog icon in
  the bottom right of the main display).  If you haven't previously connect Dash to any calendars, you'll see a
  <i>Connect</i> button, or if Dash is already connected to a calendar you'll see a <i>Change</i> button.  Click
  <i>Connect</i> or <i>Change</i>.
</p>
<p>
  You should see a list of calendar services to choose from (Microsoft 365, Google, and your device calendar).  Select
  <b>Google Workspace</b> and you will be asked to sign in.  You should then see a screen asking
  permission to grant Dash access to your account.  Dash will request the following permissions:
</p>
<ul>
  <li><b>View a list of your calendars - </b>This is necessary in order to allow you to choose the calendar(s) you want to use with Dash.</li>
  <li><b>Read and write access to your calendars - </b>This allows Dash to read meeting information from your
    calendars, and also allows you to use Dash's advanced features such as booking a room from the display and
    automatically cancelling abandoned meetings.</li>
</ul>
<p>
  Dash will not be given any further access to your Google account, and will only be able to read the information
  listed above. Once you have granted Dash permission to access your account, check out our <a routerLink="/guide/quickStart">Quick start guide</a>
  for more information on how to get started with Dash.
</p>


